import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { SlidesFilterParams, SlideType, SlidesListResponse, CreateSlideParams, UpdateSlideParams } from '@/types/slide'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import { fetchSlides, fetchSlide, createSlide, updateSlide, deleteSlide, bulkDeleteSlides, sortSlide } from '@/lib/api'

type SlideStateType = {
  data: SlideType | null
  isLoading: boolean
  error: string | null
}

type SlidesListStateType = {
  data: SlidesListResponse | null
  isLoading: boolean
  error: string | null
}

type SlidesStoreState = {
  slide: SlideStateType
  slidesList: SlidesListStateType
  filters: SlidesFilterParams
  selectedSlideIds: string[]
  setFilters: (filters: Partial<SlidesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchSlides: (params?: SlidesFilterParams) => Promise<SlidesListResponse>
  resetSlidesList: () => void

  // Sort slide
  sortSlide: (id: string, type: SortOrderEnum) => Promise<SlideType>

  // Blog Post operations
  getSlide: (id: string) => Promise<SlideType>
  createSlide: (params: CreateSlideParams) => Promise<SlideType>
  updateSlide: (id: string, params: UpdateSlideParams) => Promise<SlideType>
  deleteSlide: (id: string) => Promise<void>
  bulkDeleteSlides: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectSlide: (id: string) => void
  deselectSlide: (id: string) => void
  toggleSlideSelection: (id: string) => void
  selectAllSlides: (slides: SlideType[]) => void
  deselectAllSlides: () => void
  isSlideSelected: (id: string) => boolean
  resetSlide: () => void
}

const defaultFilters: SlidesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'order',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const initialSlideState: SlideStateType = {
  data: null,
  isLoading: false,
  error: null
}

const initialSlidesListState: SlidesListStateType = {
  data: null,
  isLoading: false,
  error: null
}

const useSlidesStore = create<SlidesStoreState>()(
  devtools((set, get) => ({
    slide: initialSlideState,
    slidesList: initialSlidesListState,
    selectedSlideIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof SlidesFilterParams]
          } else {
            updatedFilters[key as keyof SlidesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: SlidesFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as SlidesFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof SlidesFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getSlide: async id => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchSlide(id)

        set(state => ({
          slide: {
            ...state.slide,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createSlide: async params => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createSlide(params)

        set(state => ({
          slide: {
            ...state.slide,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateSlide: async (id, params) => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateSlide(id, params)

        set(state => ({
          slide: {
            ...state.slide,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteSlide: async id => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteSlide(id)

        set(state => ({
          slide: {
            ...state.slide,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetSlide: () => {
      set(state => ({
        slide: {
          ...state.slide,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectSlide: id => {
      set(state => {
        if (state.selectedSlideIds.includes(id)) {
          return state
        }
        return { selectedSlideIds: [...state.selectedSlideIds, id] }
      })
    },

    deselectSlide: id => {
      set(state => ({
        selectedSlideIds: state.selectedSlideIds.filter(slideId => slideId !== id)
      }))
    },

    toggleSlideSelection: id => {
      set(state => {
        if (state.selectedSlideIds.includes(id)) {
          return {
            selectedSlideIds: state.selectedSlideIds.filter(slideId => slideId !== id)
          }
        }
        return { selectedSlideIds: [...state.selectedSlideIds, id] }
      })
    },

    selectAllSlides: slides => {
      set(() => ({
        selectedSlideIds: slides.map(slide => slide.id)
      }))
    },

    deselectAllSlides: () => {
      set(() => ({ selectedSlideIds: [] }))
    },

    isSlideSelected: id => {
      return get().selectedSlideIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteSlides: async ids => {
      set(state => ({
        slide: {
          ...state.slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteSlides({ ids })

        set(state => ({
          slide: {
            ...state.slide,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedSlideIds: state.selectedSlideIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slide: {
            ...state.slide,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchSlides: async (params?: SlidesFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        slidesList: {
          ...state.slidesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchSlides(filtersToUse)

        set(state => ({
          slidesList: {
            ...state.slidesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          slidesList: {
            ...state.slidesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetSlidesList: () => {
      set(state => ({
        slidesList: {
          ...state.slidesList,
          data: null,
          error: null
        }
      }))
    },

    sortSlide: async (id, type) => {
      set(() => ({
        slide: {
          ...get().slide,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await sortSlide(id, type)

        set(() => ({
          slide: {
            ...get().slide,
            isLoading: false,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          slide: {
            ...initialSlideState,
            error: getErrorMessage(error)
          }
        }))
        throw error
      }
    }
  }))
)

export default useSlidesStore
